import { EventEmitter, Injectable } from '@angular/core';
import { AlertController } from '@ionic/angular';
import { Observable } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { CreateVisumComponent } from '../../../components/create-visum/create-visum.component';
import { TextKeys } from '../../i18n/text-keys';
import { ModalService } from '../modal.service';
import { ToastService } from '../toast-service';
import * as moment from 'moment';
import { Moment } from 'moment';
import { VisumBackendService } from './visum-backend.service';
import { TaskResult } from '../../models/client-models';
import { TIMEZONE } from '../language.service';
import { SessionQuery } from '../../state-mangement/session/session.query';
import { fromPromise } from 'rxjs/internal/observable/innerFrom';
import { VisumCheck, VisumListItem } from '../../models/client-models';

@Injectable({
  providedIn: 'root',
})
export class VisumService {
  constructor(
    private modalService: ModalService,
    private toastService: ToastService,
    private alertController: AlertController,
    private translateService: TranslateService,
    private visumBackendService: VisumBackendService,
    private sessionQuery: SessionQuery
  ) { }

  public getVisumInfo(
    personID: number,
    admiaUserID: number,
    logedInPersonID: number,
    date: string
  ): Observable<VisumCheck> {
    if (personID === logedInPersonID) {
      return this.visumBackendService.getVisumInfoForDay(personID, date);
    } else {
      return this.visumBackendService.getVisumInfoForDayChronoUser(
        personID,
        date,
        admiaUserID
      );
    }
  }

  public getCurrentVisum(personID: number): Observable<VisumListItem> {
    return this.visumBackendService.getCurrenVisum(personID);
  }

  public removeVisum(
    personID: number,
    admiaUserID: number,
    loggedInPersonID: number,
    date: string
  ): Observable<TaskResult> {
    if (!admiaUserID || admiaUserID === 0) {
      return this.visumBackendService.removeVisum(personID, date);
    } else {
      return this.visumBackendService.removeVisumChronoUser(
        admiaUserID,
        personID,
        date
      );
    }
  }

  public createVisum(
    currentDate: string,
    selectedDate: string,
    personID: number,
    admiaUserID: number,
    loggedInPersonID: number,
    callbackFunction: Function
  ) {
    const createVisum = new EventEmitter<string>();
    const onCancel = new EventEmitter<void>();

    this.modalService.showModal(CreateVisumComponent, {
      modalView: true,
      createVisumEvent: createVisum,
      cancelEvent: onCancel,
      selectedDate,
    });

    onCancel.subscribe(() => this.modalService.dismissModal());
    createVisum.subscribe((date) => {
      const today = moment.tz(moment(), TIMEZONE);
      if (moment.tz(date, TIMEZONE).isSameOrAfter(today)) {
        this.visumDateChangeAlert(
          currentDate,
          personID,
          admiaUserID,
          loggedInPersonID,
          callbackFunction
        );
        return;
      }

      // if (date <= currentDate) {
      //   this.toastService.presentInfoToast({
      //     message: this.translateService.instant(TextKeys.dayInfo.alreadyVisum),
      //   });
      //   this.modalService.dismissModal();
      //   return;
      // }

      this.visumBackendService
        .createCurrenVisum(personID, date, admiaUserID)
        .subscribe((value) => {
          if (value.successful) {
            this.modalService.dismissModal();
            callbackFunction(value);
            this.toastService.presentSuccessToast({ message: value.message });
          } else {
            this.toastService.presentErrorToast({ message: value.message });
          }
        });
    });
  }

  /**
   * Show pop up to ask if visum should be removed.
   *
   * @param callBack
   * @param date
   * @param personID
   * @param admiaUserID
   * @param loggedInPersonID
   * @param callBackParameter
   */
  public createVisumAlert(
    callBack: Function,
    date: string,
    personID: number,
    admiaUserID: number,
    loggedInPersonID: number,
    callBackParameter: any,
    headerText: string,
    messageText: string
  ): Observable<HTMLIonAlertElement> {
    return fromPromise(
      this.alertController.create({
        cssClass: 'my-custom-class',
        header: this.translateService.instant(headerText),
        message: this.translateService.instant(messageText),
        buttons: [
          {
            text: this.translateService.instant(TextKeys.general.cancel),
            role: 'cancel',
            cssClass: 'secondary',
            handler: () => {
              // do nothing.
            },
          },
          {
            text: this.translateService.instant(TextKeys.timeEntryForm.remove),
            cssClass: 'visum-delete-btn',
            handler: () => {
              this.removeVisum(
                personID,
                admiaUserID,
                loggedInPersonID,
                date
              ).subscribe({
                next: (data) => {
                  this.toastService.presentInfoToast({ message: data.message });
                  
                  if (!data.successful) return;
                  
                  if (callBackParameter != null) {
                    callBack(callBackParameter);
                  } else {
                    callBack();
                  }
                },
                error: (error) => {
                  error.error?.message ??
                    this.toastService.presentErrorToast({
                      message: error.error?.message,
                    });
                },
              });
            },
          },
        ],
      })
    );
  }

  showVisumDateChangeAlert(
    callbackFunction: (takeCurrentDate: boolean) => any
  ) {
    const visumMessageKey = this.sessionQuery.getVisumWithTime()
      ? TextKeys.dayInfo.futureVisumWithTime
      : TextKeys.dayInfo.futureVisum;
    fromPromise(
      this.alertController.create({
        cssClass: 'my-custom-class',
        header: this.translateService.instant(TextKeys.dayInfo.setVisum),
        message: this.translateService.instant(visumMessageKey),
        buttons: [
          {
            text: this.translateService.instant(TextKeys.general.no),
            role: 'cancel',
            cssClass: 'secondary',
            handler: () => {
              this.alertController
                .dismiss()
                .then(() => callbackFunction(false));
            },
          },
          {
            text: this.translateService.instant(TextKeys.general.yes),
            handler: () => {
              this.alertController.dismiss().then(() => callbackFunction(true));
            },
          },
        ],
      })
    ).subscribe((alert) => {
      alert
        .present()
        .catch((error) =>
          this.toastService.presentErrorToast({ message: error?.error.message })
        );
    });
  }

  public getFallbackDateWhenInFuture(): Moment {
    let yesterday = null;
    if (this.sessionQuery.getVisumWithTime()) {
      yesterday = moment.tz(moment(), TIMEZONE);
    } else {
      yesterday = moment.tz(TIMEZONE).subtract(1, 'days').endOf('day');
    }

    return yesterday;
  }

  private visumDateChangeAlert(
    currentDate: string,
    personID: number,
    admiaUserID: number,
    loggedInPersonID: number,
    callbackFunction: Function
  ) {
    this.showVisumDateChangeAlert((takeCurrentDate: boolean) => {
      if (!takeCurrentDate) {
        return;
      }

      const yesterday = this.getFallbackDateWhenInFuture();
      if (moment.tz(currentDate, TIMEZONE).isSameOrAfter(yesterday)) {
        this.toastService.presentInfoToast({
          message: this.translateService.instant(TextKeys.dayInfo.alreadyVisum),
        });
        this.modalService.dismissModal();
        return;
      }

      this.visumBackendService
        .createCurrenVisum(personID, yesterday.format(), admiaUserID)
        .subscribe((result) => {
          this.toastService.presentInfoToast({ message: result.message });
          this.modalService.dismissModal();
          callbackFunction(result);
        });
    });
  }
}
